import { Button } from "@omni/ui/components/Button/button.component";
import React, { useState } from "react";
import {
    ArrayPath,
    Controller,
    FieldArrayWithId,
    FieldValues,
    UseFieldArrayAppend
} from "react-hook-form";

import { MessageComponent } from "@/components/General/Messages/messages.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

import { MultiFileUploadFieldChildrenProps } from "./multiUploadField.component";

type FileUploadFieldProps<T extends FieldValues> =
    MultiFileUploadFieldChildrenProps<T> & {
        maxUploads: number;
        onUploadChange: (
            file: File,
            append: UseFieldArrayAppend<T, ArrayPath<T>>
        ) => Promise<void>;
        renderFieldItem: (
            fieldItem: FieldArrayWithId<T, ArrayPath<T>, "id">
        ) => React.ReactNode;
        acceptedFileTypes: Array<string>;
    };

const FileUploadField = <T extends FieldValues>({
    fields,
    control,
    disabled,
    name,
    actions,
    maxUploads,
    onUploadChange,
    renderFieldItem,
    acceptedFileTypes
}: FileUploadFieldProps<T>) => {
    const { t } = useGranite();

    const [error, setError] = useState<undefined | string>();

    const reachedMaxUploads = fields.length >= maxUploads;

    const handleOnRemove = (index: number) => () => {
        actions.remove(index);
        actions.trigger();
    };

    const handleUploadChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];
        //hacky way to allow to upload the same file again
        event.target.value = "";

        if (!file) return;

        try {
            await onUploadChange(file, actions.append);
            actions.trigger();
        } catch {
            setError(
                t("components.form.fields.multiUploadField.errors.upload")
            );
        }
    };

    return (
        <>
            {error && (
                <MessageComponent
                    message={{
                        dismissible: false,
                        id: "file-upload-field-error",
                        status: "danger",
                        text: error
                    }}
                />
            )}
            {fields.map((fieldItem, index) => {
                return (
                    <div
                        key={fieldItem.id}
                        className={"mb-1"}
                    >
                        <Controller<T>
                            control={control}
                            name={`${name}.${index}.url`}
                            render={() => {
                                return (
                                    <div>
                                        {renderFieldItem(fieldItem)}
                                        <Button
                                            className="d-inline-block ml-2"
                                            size="sm"
                                            onClick={handleOnRemove(index)}
                                        >
                                            <i className="las la-trash"></i>
                                        </Button>
                                    </div>
                                );
                            }}
                        />
                    </div>
                );
            })}
            {!reachedMaxUploads && (
                <div className="mt-2">
                    <label htmlFor={`${name}.${fields.length}.url`}>
                        <span className="btn btn-outline-primary btn-sm">
                            {t(
                                "components.form.fields.multiUploadField.addFile"
                            )}
                        </span>
                    </label>
                    <input
                        id={`${name}.${fields.length}.url`}
                        disabled={disabled}
                        type="file"
                        hidden
                        onChange={handleUploadChange}
                        accept={acceptedFileTypes.join(",")}
                    />
                </div>
            )}
        </>
    );
};

export { FileUploadField };
